import React, { useState } from 'react';
import CommonModal from '../../../Components/CommonModal';
import CommonLoader from '../../../Components/CommonLoader';
import { risk_portal_create_scenario } from '../../../services';

// 1–5 rating options shared by the likelihood / impact selects.
const SCALE_1_5 = [1, 2, 3, 4, 5].map((n) => ({ value: n, label: String(n) }));

// ScenarioForm — create a "what-if" scenario for stress testing. CommonModal
// owns the form. Create only — delete lives on the scenario card in the page.
// Page owns the toast; this reports via callbacks. Payload matches the legacy
// portal: { name, description, likelihood, impact_financial, impact_operational,
// impact_reputational, impact_regulatory, assumptions, mitigation_strategy }.
//
// Props: show, onClose, onSuccess(msg), onError(msg).
export default function ScenarioForm({ show, onClose, onSuccess, onError }) {
    const [busy, setBusy] = useState(false);

    const submit = async (data) => {
        setBusy(true);
        try {
            const payload = {
                name: (data.name || '').trim(),
                description: (data.description || '').trim(),
                likelihood: Number(data.likelihood) || 3,
                impact_financial: data.impact_financial === '' ? null : Number(data.impact_financial),
                impact_operational: Number(data.impact_operational) || 3,
                impact_reputational: Number(data.impact_reputational) || 3,
                impact_regulatory: Number(data.impact_regulatory) || 3,
                assumptions: (data.assumptions || '').trim(),
                mitigation_strategy: (data.mitigation_strategy || '').trim(),
            };
            await risk_portal_create_scenario(payload);
            onSuccess?.(`Scenario "${payload.name}" created`);
            onClose?.();
        } catch (e) {
            onError?.(e?.response?.data?.error?.message || e?.response?.data?.response_data?.message || 'Failed to create scenario');
        } finally {
            setBusy(false);
        }
    };

    return (
        <CommonModal
            show={show}
            mid
            onHide={onClose}
            title="Create Scenario"
            subtitle='Model a "what-if" scenario for an emerging risk and its potential impact.'
            fields={[
                { name: 'name', label: 'Scenario Name', type: 'text', required: 'Scenario name is required.', placeholder: 'e.g. Ransomware on critical infrastructure' },
                { name: 'description', label: 'Description', type: 'textarea', required: 'Description is required.', placeholder: 'Describe the scenario and its conditions…' },
                { name: 'likelihood', label: 'Likelihood (1–5)', type: 'select', half: true, options: SCALE_1_5 },
                { name: 'impact_financial', label: 'Financial Impact ($)', type: 'number', half: true, placeholder: 'e.g. 500000' },
                { name: 'impact_operational', label: 'Operational (1–5)', type: 'select', half: true, options: SCALE_1_5 },
                { name: 'impact_reputational', label: 'Reputational (1–5)', type: 'select', half: true, options: SCALE_1_5 },
                { name: 'impact_regulatory', label: 'Regulatory (1–5)', type: 'select', half: true, options: SCALE_1_5 },
                { name: 'assumptions', label: 'Assumptions', type: 'textarea' },
                { name: 'mitigation_strategy', label: 'Mitigation Strategy', type: 'textarea' },
            ]}
            defaultValues={{
                name: '', description: '', likelihood: 3, impact_financial: '',
                impact_operational: 3, impact_reputational: 3, impact_regulatory: 3,
                assumptions: '', mitigation_strategy: '',
            }}
            onSubmit={submit}
            buttons={[
                { text: 'Cancel', variant: 'secondary', onClick: onClose },
                { text: 'Create Scenario', variant: 'primary', type: 'submit' },
            ]}
        >
            {busy && <CommonLoader overlay label="Saving…" />}
        </CommonModal>
    );
}
